<!--
  ShippingAddressEditor.vue -- Phase 1 of the shipping-address redesign
  (docs/planning/2026-07-31-shipping-address-and-checkout-shipping-redesign.md).

  A reusable structured shipping-address editor for every owner of the
  structured ``ship_to_*`` columns:
    - the company (Client) address on ClientDetailPage.vue
    - the per-department (ClientDepartment) address on ClientDepartmentsPanel.vue
    - the per-store (Store) address on the store editor's Checkout > Shipping

  All owners carry the SAME structured ``ship_to_*`` columns, so a single
  editor concept serves all of them (design philosophy: one mechanism per concept).

  Contract with the parent
  -------------------------
  Props:
    - ``endpoint`` (String, required): the PUT/GET path for this address, e.g.
      ``/clients/7/shipping-address`` or
      ``/clients/7/departments/3/shipping-address``.
    - ``label`` (String): heading shown above the fields.
    - ``disabled`` (Boolean): read-only mode (caller lacks client.update).

  On save the editor validates the address through the wired ShipEngine
  service (POST /address_validation/validateAddress) and shows the shared
  ValidateAddressDialog so the user can accept the normalized address or keep
  their original (the exact pattern the store-locations editor uses). It then
  PUTs the chosen address to ``endpoint``; the SERVER re-validates and returns
  a ``validated`` flag, which the editor surfaces. A "Validated" chip signals
  the address is a usable ship-to fallback (Phase 3's readiness gate).
-->
<template>
  <div class="ship-addr" data-testid="shipping-address-editor">
    <div class="ship-addr__head">
      <h4 class="ship-addr__label">{{ label }}</h4>
      <span
        v-if="validated"
        class="ship-addr__chip ship-addr__chip--ok"
        data-testid="shipping-address-validated-chip"
      >
        Validated
      </span>
      <span
        v-else-if="hasAddress"
        class="ship-addr__chip ship-addr__chip--warn"
        data-testid="shipping-address-unvalidated-chip"
      >
        Not validated
      </span>
    </div>

    <div class="ship-addr__grid">
      <label class="ship-addr__field">
        <span>Recipient name</span>
        <input
          v-model="form.recipient_name"
          type="text"
          :disabled="disabled"
          data-testid="shipping-address-recipient-name"
        />
      </label>
      <label class="ship-addr__field">
        <span>Email</span>
        <input
          v-model="form.email"
          type="email"
          :disabled="disabled"
          data-testid="shipping-address-email"
        />
      </label>
      <label class="ship-addr__field ship-addr__field--wide">
        <span>Address line 1 *</span>
        <input
          v-model="form.address1"
          type="text"
          :disabled="disabled"
          data-testid="shipping-address-address1"
        />
      </label>
      <label class="ship-addr__field ship-addr__field--wide">
        <span>Address line 2</span>
        <input
          v-model="form.address2"
          type="text"
          :disabled="disabled"
          data-testid="shipping-address-address2"
        />
      </label>
      <label class="ship-addr__field">
        <span>City *</span>
        <input
          v-model="form.city"
          type="text"
          :disabled="disabled"
          data-testid="shipping-address-city"
        />
      </label>
      <label class="ship-addr__field">
        <span>State</span>
        <input
          v-model="form.state"
          type="text"
          :disabled="disabled"
          data-testid="shipping-address-state"
        />
      </label>
      <label class="ship-addr__field">
        <span>ZIP code *</span>
        <input
          v-model="form.postal_code"
          type="text"
          :disabled="disabled"
          data-testid="shipping-address-postal-code"
        />
      </label>
      <label class="ship-addr__field">
        <span>Country</span>
        <input
          v-model="form.country"
          type="text"
          :disabled="disabled"
          data-testid="shipping-address-country"
        />
      </label>
    </div>

    <div class="ship-addr__actions">
      <button
        v-if="!disabled"
        type="button"
        class="ship-addr__save"
        :disabled="saving || !canSave"
        data-testid="shipping-address-save"
        @click="onSaveClicked"
      >
        {{ saving ? 'Saving...' : 'Save shipping address' }}
      </button>
    </div>

    <div
      v-if="saveError"
      class="ship-addr__error"
      data-testid="shipping-address-error"
    >
      {{ saveError }}
    </div>

    <!--
      Shared validation dialog (reused from the store-locations editor). On
      "Use Validated" we adopt the normalized address; on "Use Original" we
      keep what the user typed. Either way we then PUT to the server.
    -->
    <ValidateAddressDialog
      ref="validateDialog"
      :original-location="dialogLocation"
      data-testid="shipping-address-validate-dialog"
      @useValidatedAddress="onUseValidated"
      @useOriginalAddress="onUseOriginal"
    />
  </div>
</template>

<script>
import api from '@/services/api'
import ValidateAddressDialog from '@/pages/distributor/store-editor/StoreLocations/ValidateAddressDialog.vue'

function blankForm() {
  return {
    recipient_name: '',
    email: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
  }
}

export default {
  name: 'ShippingAddressEditor',
  components: { ValidateAddressDialog },
  props: {
    endpoint: { type: String, required: true },
    label: { type: String, default: 'Shipping address' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      form: blankForm(),
      saving: false,
      saveError: null,
      validated: false,
      loaded: false,
    }
  },
  computed: {
    // The postal core is required for a deliverable, validatable address.
    canSave() {
      return Boolean(
        (this.form.address1 || '').trim() &&
          (this.form.city || '').trim() &&
          (this.form.postal_code || '').trim(),
      )
    },
    hasAddress() {
      return Boolean((this.form.address1 || '').trim())
    },
    // ValidateAddressDialog reads street_and_number / suite_unit_etc / city /
    // region / zip_code / country -- map our ship_to_* field names onto its
    // shape so the dialog + the /validateAddress endpoint see what they expect.
    dialogLocation() {
      return {
        street_and_number: this.form.address1 || '',
        suite_unit_etc: this.form.address2 || '',
        city: this.form.city || '',
        region: this.form.state || '',
        zip_code: this.form.postal_code || '',
        country: this.form.country || 'US',
      }
    },
  },
  watch: {
    endpoint() {
      this.load()
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      try {
        const res = await api.get(this.endpoint)
        const addr = res?.data?.shipping_address
        if (addr) {
          this.form = {
            recipient_name: addr.recipient_name || '',
            email: addr.email || '',
            address1: addr.address1 || '',
            address2: addr.address2 || '',
            city: addr.city || '',
            state: addr.state || '',
            postal_code: addr.postal_code || '',
            country: addr.country || 'US',
          }
          this.validated = Boolean(addr.validated)
        }
      } catch (e) {
        // Non-fatal: a fresh owner with no address yet renders empty fields.
      } finally {
        this.loaded = true
      }
    },
    // Pre-fill the form from another owner's address (the store editor's
    // "Use the client's company address"). Fills only: nothing is saved, and
    // the source's validation stamp does not travel -- THIS owner's address
    // is validated only when the user saves it through the validated path.
    fillFrom(addr) {
      if (!addr) return
      this.form = {
        recipient_name: addr.recipient_name || '',
        email: addr.email || '',
        address1: addr.address1 || '',
        address2: addr.address2 || '',
        city: addr.city || '',
        state: addr.state || '',
        postal_code: addr.postal_code || '',
        country: addr.country || 'US',
      }
      this.validated = false
      this.saveError = null
    },
    onSaveClicked() {
      if (!this.canSave || this.saving) return
      this.saveError = null
      // Kick off validation via the shared dialog. It POSTs to
      // /address_validation/validateAddress and either shows the confirm
      // dialog or (on service outage) emits useOriginalAddress directly.
      this.$refs.validateDialog.show()
    },
    onUseValidated(validatedAddress) {
      // Adopt ShipEngine's normalized fields, then persist.
      this.form = {
        ...this.form,
        address1: validatedAddress.street_and_number || this.form.address1,
        address2: validatedAddress.suite_unit_etc || this.form.address2,
        city: validatedAddress.city || this.form.city,
        state: validatedAddress.region || this.form.state,
        postal_code: validatedAddress.zip_code || this.form.postal_code,
        country: validatedAddress.country || this.form.country,
      }
      this.persist()
    },
    onUseOriginal() {
      // Keep what the user typed and persist as-is. The server re-validates
      // and decides whether to stamp the address validated.
      this.persist()
    },
    async persist() {
      this.saving = true
      this.saveError = null
      try {
        const res = await api.put(this.endpoint, {
          recipient_name: this.form.recipient_name || null,
          email: this.form.email || null,
          address1: this.form.address1 || null,
          address2: this.form.address2 || null,
          city: this.form.city || null,
          state: this.form.state || null,
          postal_code: this.form.postal_code || null,
          country: this.form.country || 'US',
        })
        this.validated = Boolean(res?.data?.validated)
        const addr = res?.data?.shipping_address
        if (addr) {
          this.form = {
            recipient_name: addr.recipient_name || '',
            email: addr.email || '',
            address1: addr.address1 || '',
            address2: addr.address2 || '',
            city: addr.city || '',
            state: addr.state || '',
            postal_code: addr.postal_code || '',
            country: addr.country || 'US',
          }
        }
        this.$emit('saved', { validated: this.validated })
      } catch (e) {
        const data = e?.response?.data
        this.saveError =
          (Array.isArray(data?.errors) &&
            data.errors.length &&
            data.errors[0]?.detail) ||
          data?.detail ||
          data?.message ||
          'Could not save the shipping address.'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.ship-addr {
  border-top: 1px solid var(--tf-border, #d0d4dc);
  padding-top: 16px;
  margin-top: 8px;
}
.ship-addr__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.ship-addr__label {
  margin: 0;
  font-size: 14px;
}
.ship-addr__chip {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
}
.ship-addr__chip--ok {
  background: #e6f4ea;
  color: #1e7e34;
}
.ship-addr__chip--warn {
  background: #fff4e5;
  color: #b45309;
}
.ship-addr__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.ship-addr__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
}
.ship-addr__field--wide {
  grid-column: 1 / -1;
}
.ship-addr__field input {
  padding: 6px 10px;
  border: 1px solid var(--tf-border, #d0d4dc);
  border-radius: var(--tf-radius-xs, 4px);
  font-size: 13px;
  font-family: inherit;
}
.ship-addr__field input:disabled {
  background: var(--tf-surface-alt, #f4f5f7);
  color: var(--tf-fg-3, #8a8f98);
}
.ship-addr__actions {
  margin-top: 12px;
}
.ship-addr__save {
  padding: 6px 12px;
  border: 1px solid var(--tf-accent, #2c5fff);
  border-radius: var(--tf-radius-xs, 4px);
  background: var(--tf-accent, #2c5fff);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}
.ship-addr__save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.ship-addr__error {
  margin-top: 8px;
  color: #c0392b;
  font-size: 12px;
}
</style>
